<template>
  <div class="pages-ccd-tenancy-view">
    <TenancyHeader>
      <template slot="header-top">
        <HKLHeader
          :label-left-primary="tenancyHklLeftPrimaryLabel"
          link-class-left-primary="back"
          @left-primary="onTenancyHklLeftPrimary"
          :label-right-leading="$t('listNavigation.previousRecord')"
          :label-right-trailing="$t('listNavigation.nextRecord')"
          :navigation-entries="tenancyListNavEntriesForHeader"
          :current-record-id="$route.params.id"
          @right-leading="onTenancyListNavEntry"
          @right-trailing="onTenancyListNavEntry"
        />
      </template>
      <Sticky
        class="sticky-header"
        extendStickyClass="sticky-button--group"
        :fullWidth="false"
        :zIndex="Number(101)"
        :align="'right'"
      >
        <button
          v-if="showEditBtn"
          v-hkldreset
          type="button"
          class="btn btn--orange edit"
          :disabled="disabledEditBtn"
          :class="{'tenant-edit': isTenant || (canCreateProjects && !hiddenCreateButton)}"
          @click="() => goEdit()"
        >
          {{ $t('action.edit') }}
        </button>
        <button
          v-if="isTenantOrMarketing && isContactTab && isTenantOrMarketingEditing"
          v-hkldreset
          type="button"
          class="btn btn--darkblue edit tenant-edit"
          @click="() => cancelEdit()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="isTenantOrMarketing && isContactTab && isTenantOrMarketingEditing"
          v-hkldreset
          type="button"
          class="btn btn--orange edit tenant-edit"
          @click="() => save()"
        >
          {{ $t('action.save') }}
        </button>
        <button
          v-if="canCreateProjects && !hiddenCreateButton && enableTenantProjectsFeature"
          v-hkldreset
          type="button"
          class="btn btn--orange edit"
          @click="() => openTermsConditions()"
        >
          {{ $t('newProject') }}
        </button>
      </Sticky>
    </TenancyHeader>

    <div class="tabs-container-wrapper">
      <DynamicTabsContainer
        ref="dynamicTabsContainer"
        :currentTabIndex="currentTabIndex"
        :tabTemplates="currentTabTemplates"
        class="tabs-container"
        @tabIndex="(...args) => getCurrentTab(...args)"
        @tabClick="(...args) => onActiveTab(...args)"
      />
      <TermsAndConditions
        v-if="canAccessProjects"
        ref="tncTenancyPage"
        modalId="tncTenancyPage"
        :companyName="companyName"
        :isNewProject="true"
        :acceptCallback="termsConditionsCallback"
        :skipCallback="termsConditionsCallback"
      />
    </div>
    <Modal
      ref="create-tenantproject-without-premisespair-hint"
      modalId="create-tenantproject-without-premisespair-hint"
      :showCloseBtn="false"
      modalType="customized-modal"
    >
      <div slot="content">
        <pre class="font-bold">{{ $t('tenantProjects.withoutPremisesPair') }}</pre>
      </div>
      <div slot="btns">
        <button
          class="btn btn--orange"
          @click="() => closeModal('create-tenantproject-without-premisespair-hint')"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </Modal>
    <ActionConfirmModal
      ref="personalInfoProtectModal"
      :confirmTitle="$t('pipl.content')"
      :confirmBtnName="$t('action.yes')"
      :cancelBtnName="$t('action.cancel')"
      modalId="personal-info-protect-modal"
    />
    <Modal
      ref="emptyContactHint"
      modalId="empty-contact-hint"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ emptyContactHintContent }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="submit"
          class="btn btn--orange"
          @click="() => closeModal('emptyContactHint')"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { ref, provide } from 'vue-demi'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import HKLHeader from '@/controls/HKLHeader'
import { LIST_NAV_KIND, mergeListNavigationHeaderPaths, pushListNavigationEntry, resolveNavigationKind } from '@/constants/listNavigation'
import { tenancyRecordsRouteNames } from '@/constants/router'
import { VIEW_ALL_RESUME_SEARCH_QUERY } from '@/constants/viewAllTableCache'
import { getKey } from '@/api/common/request'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import DynamicTabsContainer from '@/controls/DynamicTabsContainer.vue'
import Modal from '@/controls/Modal'
import Sticky from '@/controls/Sticky'
import LeaseInformation from '@/modules/ccd/tenancy/edit/LeaseInformation'
import TenancyAllContactsEdit from '@/modules/ccd/tenancy/edit/TenancyAllContactsEdit'
import TenancyHeader from '@/modules/ccd/tenancy/TenancyHeader'
import TenancyTenantInformation from '@/modules/ccd/tenancy/tenant/TenancyTenantInformation'
import { getTenantTabIndexByName } from '@/modules/ccd/tenancy/utils'
import TenancyAllContacts from '@/modules/ccd/tenancy/view/TenancyAllContacts'
import TenancyAllProjects from '@/modules/ccd/tenancy/view/TenancyAllProjects'
import TenancyStandaloneWorkPermits from '@/modules/ccd/tenancy/view/TenancyStandaloneWorkPermits'
import TenancySustainabilityInitiatives from '@/modules/ccd/tenancy/view/TenancySustainabilityInitiatives'
import TenancyViewInformation from '@/modules/ccd/tenancy/view/TenancyViewInformation'
import { buildContactRequest } from '@/modules/ccd/utils'
import UserManagement from '@/pages/ccd/UserManagement'
import TermsAndConditions from '@/pages/tenants/TermsAndConditions'
import { utils as formUtils } from '@/plugin/InputChange'
import store from '@/store'
import mutationTypes from '@/store/mutation-types'
import { aclHelper, ccdPermsTypes, tmsPermsTypes } from '@/utils/aclHelper'
import { ENGINEER_TENANCY_TABS, hotbTenantViewableStatus, tenancyEditTabs, tenancyTabsViewToEdit, tenancyViewTabs } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import { tenancyType } from '@/utils/locale'
import navigation from '@/utils/navigation'
import { TR_EMPTY_CONTACT_WARN_MAP } from '@/modules/ccd/tenancy/consts'
import { TR_EVENT_KEYS, TR_PROVIDE_KEYS } from './tenancy.consts'
import { useIsOnlyTenancyRepresentative } from './tenancy.hook'

/** Prev/next for All Tenancy Records only when entering from list, in-view prev/next, or edit return — not deep links. */
const ROUTE_NAMES_PRESERVE_TENANCY_LIST_NAV = new Set([
  tenancyRecordsRouteNames.allTenancyRecs,
  tenancyRecordsRouteNames.tenantAllTenancyRecs,
  tenancyRecordsRouteNames.ccdTenancyView,
  tenancyRecordsRouteNames.ccdTenancyTenantView,
  tenancyRecordsRouteNames.ccdTenancyEdit,
])

function clearStaleTenancyRecordListNavigationIfUnanchoredEnter (fromRoute) {
  const fromName = fromRoute && fromRoute.name
  if (fromName != null && fromName !== '' && ROUTE_NAMES_PRESERVE_TENANCY_LIST_NAV.has(String(fromName))) {
    return
  }
  const seq = store.getters['listNavigation/listNavigationSequence'] || []
  if (!seq.length) return
  const allTenancyRecord = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.TENANCY_RECORD)
  if (!allTenancyRecord) return
  store.dispatch('listNavigation/clearListNavigationSequence')
}

export default {
  name: 'TenancyView',
  components: {
    ActionConfirmModal,
    DynamicTabsContainer,
    HKLHeader,
    Modal,
    Sticky,
    TenancyHeader,
    TermsAndConditions,
  },
  provide () {
    return {
      standaloneTabIndex: this.isEngineer
        ? this.tabTemplates.map(item => item.tabName).indexOf(this.standaloneWorkPermitsName)
        : this.tenantViewTabTemplates.map(item => item.tabName).indexOf(this.standaloneWorkPermitsName),
    }
  },
  async beforeRouteEnter (to, from, next) {
    try {
      if (from && from.name === 'engineerDashboard' && from.fullPath) {
        store.dispatch('listNavigation/setTenancyBackPath', from.fullPath)
      }
      clearStaleTenancyRecordListNavigationIfUnanchoredEnter(from)
      await store['_actions']['ccd/loadTenancy'][0](to.params.id)
      next()
    } catch (err) {
      next(err)
      throw err
    }
  },
  async beforeRouteUpdate (to, from, next) {
    if (String(to.params.id) === String(from.params.id)) {
      next()
      return
    }
    try {
      this.clearTenancyBackPath()
      await this.loadTenancy(to.params.id)
      await this.tryRestoreSuspendedTenancyRecordSequence({ tenancyRouteId: to.params.id })
      this.syncListNavigationSequenceWithRoute()
      next()
    } catch (err) {
      next(err)
    }
  },
  beforeRouteLeave (to, from, next) {
    if (to.name !== tenancyRecordsRouteNames.ccdTenancyEdit) {
      this.clearTenancyBackPath()
    }
    next()
  },
  setup () {
    provide(TR_PROVIDE_KEYS.DETAIL_EDITING, false)
    let returnData = {}
    const disabledEditBtn = ref(false)
    EventBus.on(TR_EVENT_KEYS.TR_REPS_IS_EDITING, (isEditing) => {
      disabledEditBtn.value = isEditing
    })
    const isOnlyTenancyRepresentative = useIsOnlyTenancyRepresentative()
    returnData = {
      ...returnData,
      disabledEditBtn,
      isOnlyTenancyRepresentative,
    }

    return returnData
  },
  data () {
    const routeParamsId = this.$router.currentRoute.params.id
    return {
      isTenantOrMarketingEditing: false,
      routeParamsId,
      currentTabIndex: -1,
      hotbTenantViewableStatus,
      tenancyViewTabs,
      deep,
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy', 'canTenantEditContact', 'canAccessAllProjects']),
    ...mapGetters('listNavigation', ['listNavigationSequence', 'tenancyBackPath']),
    ...mapGetters('user', ['isTenant', 'currentUser', 'isEngineer', 'visitedTenancyRecord']),
    ...mapGetters('form', ['isPageDirty']),
    ...mapGetters('regions', ['enableTenantProjectsFeature', 'enableWorkPermitsFeature', 'enableGreenOperationFeature']),
    userManagementTabName () {
      return this.$t('tenancyView.userManagement')
    },
    allProjectsTabName () {
      return this.$t('tenancyView.allProjects')
    },
    standaloneWorkPermitsName () {
      return this.$t('standaloneWorkPermits.name')
    },
    tenancyInfoTabName () {
      return this.$t('tenancyView.tenancyInformation')
    },
    allContactsTabName () {
      return this.$t('tenancyView.allContacts')
    },
    linkedLeasesTabName () {
      return this.$t('lease.linkedLeases')
    },
    emptyContactHintContent () {
      const type = this.currentTenancy.type || ''
      return this.$t(`hint.contact.hintEmptyContactWarn.${type.toLowerCase()}`)
    },
    isMaster () {
      const masterAccount = this.accounts.filter(account => account.TenancyAccount.isAdmin)[0] || {}
      return this.currentUser.id === masterAccount.id
    },
    accounts () {
      return (this.currentTenancy && this.currentTenancy.accounts) || []
    },
    canAccessStandaloneWP () {
      if (this.isEngineer) {
        return true
      } else {
        return (this.accounts || []).some(
          (account) => !lodash.isEmpty(account.TenancyAccount.perms.StandaloneWorkPermits) &&
            account.id === this.currentUser.id)
      }
    },
    canTenantAccessGreenOperation () {
      return this.isMaster || (this.accounts || []).some(
        (account) => !lodash.isEmpty(account.TenancyAccount.perms.GreenOperation) &&
            account.id === this.currentUser.id)
    },
    selfSubAccount () {
      return this.accounts.filter(account => account.id === this.currentUser.id)[0]
    },
    selfPerms () {
      return this.selfSubAccount && this.selfSubAccount.TenancyAccount.perms
    },
    companyId () {
      return deep(this.currentTenancy, 'company.id')
    },
    companyName () {
      return deep(this.currentTenancy, 'company.name')
    },
    tenancyId () {
      return this.$route.params.id
    },
    tenancyRecordNavPathsByKind () {
      const base = this.isTenant ? '/tenants/tenancies' : '/tenancies/view'
      return { [LIST_NAV_KIND.TENANCY_RECORD]: base }
    },
    /** Prev/Next only when sequence came from View All tenancy records (same kind + includes this tenancy) */
    hasTenancyListNavigation () {
      if (this.isTenant) return false
      const seq = this.listNavigationSequence || []
      const cur = String(this.$route.params.id)
      if (!seq.length) return false
      const allTenancyRecord = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.TENANCY_RECORD)
      const containsCur = seq.some(e => String(e.id) === cur)
      return allTenancyRecord && containsCur
    },
    tenancyListNavEntriesForHeader () {
      return this.hasTenancyListNavigation ? this.listNavigationSequence : []
    },
    /** For `@right-leading` / `@right-trailing` → `pushListNavigationEntry`. */
    tenancyListNavMergedPaths () {
      return mergeListNavigationHeaderPaths(this.tenancyRecordNavPathsByKind, '', '')
    },
    /** With committed LIST_NAVIGATION sequence from All Tenancy Records → search-results wording; else `< Back` + goBack. */
    tenancyHklLeftPrimaryLabel () {
      return this.hasTenancyListNavigation
        ? this.$t('listNavigation.backToSearchResults')
        : this.$t('back')
    },
    showEditBtn () {
      if (this.isTenant) {
        return this.canTenantEditContact && this.isContactTab && !this.isTenantOrMarketingEditing
      }
      if (this.isOnlyTenancyRepresentative) {
        return false
      }
      const canMarketingEdit = this.isMarketing && !this.isTenantOrMarketingEditing && this.isContactTab
      return this.canEditTenancy || canMarketingEdit
    },
    isTenantOrMarketing () {
      return this.canTenantEditContact || this.isMarketing
    },
    isMarketing () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts) && !this.canEditTenancy && this.isRetail
    },
    isRetail () {
      return this.currentTenancy.type === tenancyType.Retail
    },
    canEditTenancy () {
      const hasUpdateAccess = aclHelper.getAccess(ccdPermsTypes.tenancy.update) ||
        aclHelper.getAccess(ccdPermsTypes.tenancy.inviteMaster) ||
        aclHelper.getAccess(ccdPermsTypes.tenancy.linkLeases) ||
        aclHelper.getAccess(ccdPermsTypes.tenancy.linkContacts) ||
        aclHelper.getAccess(ccdPermsTypes.tenancy.updateTenancyMasterAccount)
      return hasUpdateAccess && (!this.isTenant || this.currentTabIndex !== 2)
    },
    canAccessProjects () {
      return this.canAccessAllProjects
    },
    canEngineerCreateUpdateProjects () {
      return aclHelper.getAccess(tmsPermsTypes.project.createUpdate)
    },
    canCreateProjects () {
      return this.canAccessProjects || this.canEngineerCreateUpdateProjects
    },
    canAccessHotbs () {
      return this.isMaster || !!(this.selfPerms && this.selfPerms.HandoverTakeback)
    },
    canEngineerAccessProjects () {
      return aclHelper.getAccess(tmsPermsTypes.project.read)
    },
    canEngineerAccessHotbs () {
      return aclHelper.getAccess(tmsPermsTypes.hotb.read)
    },
    allowInviteMaster () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.inviteMaster)
    },
    hiddenCreateButton () {
      return this.isTenant && parseInt(this.currentTabIndex) === this.tenantViewTabTemplates.length - 1
    },
    isContactTab () {
      const contactTabIndex = this.currentTabTemplates.findIndex(({ tabName }) => tabName === this.allContactsTabName)
      return this.currentTabIndex === contactTabIndex
    },
    premisesPair () {
      return lodash.get(this.currentTenancy, 'premisesDetailList', [])
    },
    currentTabTemplates () {
      return !this.isTenant ? this.tabTemplates : this.tenantViewTabTemplates
    },
    tabTemplates () {
      let tmpTemplates = [{
        tabCName: ENGINEER_TENANCY_TABS.tenancyInformation,
        tabName: this.tenancyInfoTabName,
        component: TenancyViewInformation,
        params: { tenancyId: this.tenancyId },
      }, {
        tabName: this.allContactsTabName,
        tabCName: ENGINEER_TENANCY_TABS.allContacts,
        component: TenancyAllContacts,
        ref: 'tenancyAllContacts',
        params: {
          tenancyId: this.tenancyId,
          allowLinkContacts: this.isMarketing,
          canEditMarketing: this.isTenantOrMarketingEditing,
          linkFunc: this.goToLinkContact,
        },
      }, {
        tabName: this.linkedLeasesTabName,
        tabCName: ENGINEER_TENANCY_TABS.leases,
        component: LeaseInformation,
        params: { isEdit: false },
      }, {
        tabName: this.userManagementTabName,
        tabCName: ENGINEER_TENANCY_TABS.userManagement,
        component: UserManagement,
        params: { allowInviteMaster: this.allowInviteMaster, createProject: this.openTermsConditions },
      }]
      if (this.enableTenantProjectsFeature) {
        tmpTemplates.splice(2, 0, {
          tabName: this.allProjectsTabName,
          tabCName: ENGINEER_TENANCY_TABS.allProjects,
          component: TenancyAllProjects,
          params: { canAccessProjects: this.canEngineerAccessProjects, canAccessHotbs: this.canEngineerAccessHotbs },
        })
      }
      if (this.enableWorkPermitsFeature) {
        tmpTemplates.splice(tmpTemplates.length - 1, 0, {
          tabName: this.standaloneWorkPermitsName,
          tabCName: ENGINEER_TENANCY_TABS.standaloneWorkPermits,
          component: TenancyStandaloneWorkPermits,
        })
      }
      if (this.enableGreenOperationFeature) {
        const startIndex = this.checkIfHasAllProjectTab(tmpTemplates) ? 3 : 2
        tmpTemplates.splice(startIndex, 0, {
          tabName: this.$t('tenancyView.sustainabilityInitiatives'),
          tabCName: ENGINEER_TENANCY_TABS.sustainabilityInitiatives,
          component: TenancySustainabilityInitiatives,
        })
      }
      return tmpTemplates
    },
    tenantViewTabTemplates () {
      let tmpTenantTabs = [{
        tabName: this.tenancyInfoTabName,
        component: TenancyTenantInformation,
        params: { tenancyId: this.tenancyId },
      }, {
        tabName: this.allContactsTabName,
        component: this.isTenantOrMarketingEditing ? TenancyAllContactsEdit : TenancyAllContacts,
        ref: 'tenancyAllContactsEdit',
        params: {
          tenancyId: this.tenancyId,
          companyId: this.companyId,
          allowLinkContacts: this.canTenantEditContact,
          linkFunc: this.goToLinkContact,
          isRetail: this.isRetail,
        },
      }, {
        tabName: this.userManagementTabName,
        component: UserManagement,
        params: { isMaster: this.isMaster, createProject: this.openTermsConditions },
      }]
      if (this.enableTenantProjectsFeature && (this.isMaster || (this.canAccessHotbs || this.canAccessProjects))) {
        tmpTenantTabs.splice(2, 0, {
          tabName: this.allProjectsTabName,
          component: TenancyAllProjects,
          params: {
            canAccessProjects: this.canAccessProjects,
            canAccessHotbs: this.canAccessHotbs,
            statusList: this.hotbTenantViewableStatus,
          },
          showCondition: this.canAccessProjects || this.canAccessHotbs,
        })
      }

      if (this.enableWorkPermitsFeature && (this.isMaster || this.canAccessStandaloneWP)) {
        tmpTenantTabs.splice(tmpTenantTabs.length - 1, 0, {
          tabName: this.standaloneWorkPermitsName,
          component: TenancyStandaloneWorkPermits,
          showCondition: this.enableWorkPermitsFeature && this.canAccessStandaloneWP,
        })
      }
      if (this.enableGreenOperationFeature && (this.canTenantAccessGreenOperation)) {
        const startIndex = this.checkIfHasAllProjectTab(tmpTenantTabs) ? 3 : 2
        tmpTenantTabs.splice(startIndex, 0, {
          tabName: this.$t('tenancyView.sustainabilityInitiatives'),
          component: TenancySustainabilityInitiatives,
        })
      }
      return tmpTenantTabs
    },
  },
  async mounted () {
    window.scrollTo(0, 0)
    const routeParams = this.$router.currentRoute.params
    this.showEmptyContactWarn()
    await this.resetQueryAllIfNotMatch(this.currentTenancy.id)
    await this.tenantProjectResetQueryAll(this.currentTenancy.id)
    let tabIndex = 0
    const query = lodash.get(this.$router, 'currentRoute.query', {})
    if (query.tabName && this.isTenant) {
      tabIndex = getTenantTabIndexByName(query.tabName)
    }
    if (query.tabIndex) {
      tabIndex = query.tabIndex
    }
    const enableContactEditInQuery = this.$router.currentRoute.query.isTenantOrMarketingEditing

    if (this.$refs['dynamicTabsContainer'] && tabIndex) {
      this.$router.replace({path: this.$router.currentRoute.path}, async () => {
        EventBus.emit('common-tabs-container-select-tab', `#tab-${tabIndex}`, parseInt(tabIndex, 10))
        this.currentTabIndex = parseInt(tabIndex, 10)
        await this.addTenancyContacts(routeParams, enableContactEditInQuery)
        await this.tryRestoreSuspendedTenancyRecordSequence({ tenancyRouteId: this.$route.params.id })
        this.syncListNavigationSequenceWithRoute()
      })
      return
    }
    await this.addTenancyContacts(routeParams, enableContactEditInQuery)
    await this.tryRestoreSuspendedTenancyRecordSequence({ tenancyRouteId: this.$route.params.id })
    this.syncListNavigationSequenceWithRoute()
  },
  methods: {
    ...mapActions('listNavigation', [
      'clearListNavigationSequence',
      'clearTenancyBackPath',
      'tryRestoreSuspendedTenancyRecordSequence',
    ]),
    ...mapActions('ccd', [
      'loadTenancy',
      'updateTenancyContacts',
      'addTenancyContactsToStore',
    ]),
    ...mapActions('tenantProject', {
      tenantProjectResetQueryAll: 'resetQueryAllIfNotMatch',
    }),
    ...mapActions('hotb', ['resetQueryAllIfNotMatch']),
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
    }),
    ...mapMutations('ccd', {
      setCurrentTenancy: mutationTypes.TENANCY_UPDATE_CURRENT,
    }),
    ...mapMutations('user', {
      updateVisitedTR: mutationTypes.USER_VISIT_TR,
    }),
    navToAllTenancyRecords () {
      const name = this.isTenant ? 'tenantAllTenancyRecs' : 'allTenancyRecs'
      navigation.toPage(name, {}, { [VIEW_ALL_RESUME_SEARCH_QUERY]: '1' })
    },
    onTenancyHklLeftPrimary () {
      if (this.hasTenancyListNavigation) {
        this.clearTenancyBackPath()
        this.navToAllTenancyRecords()
        return
      }
      const backPath = this.tenancyBackPath
      this.clearTenancyBackPath()
      if (backPath) {
        this.$router.replace(backPath)
        return
      }
      this.$router.goBack()
    },
    onTenancyListNavEntry (entry) {
      pushListNavigationEntry(this.$router, this.$route, this.tenancyListNavMergedPaths, entry)
    },
    checkIfHasAllProjectTab (tabs) {
      return tabs.findIndex(item => item.tabName === this.allProjectsTabName) > -1
    },
    async openTermsConditions () {
      if (this.hasPremisesPair() && !this.isExpired()) {
        if (this.isTenant) {
          this.$refs.tncTenancyPage.openModal()
        } else {
          await this.createTenantProjectAndRouterTo(false)
        }
      } else {
        this.$refs['create-tenantproject-without-premisespair-hint'].open()
      }
    },
    closeModal (modelName) {
      this.$refs[modelName].close()
    },
    hasPremisesPair () {
      return !lodash.isEmpty(this.premisesPair)
    },
    isExpired () {
      let expired = true
      this.premisesPair.map(item => {
        if (dateUtil.isBeforeEndDate(item.endDate)) {
          expired = false
        }
      })
      return expired
    },
    async termsConditionsCallback (isAccept) {
      await this.createTenantProjectAndRouterTo(isAccept)
    },

    async createTenantProjectAndRouterTo (isAccept) {
      const id = await getKey()
      const isNewProject = true
      this.$router.push({
        name: this.isTenant ? 'createTenantProject' : 'editTenantProject',
        params: { id },
        query: {
          isAccept,
          isNewProject,
          companyName: this.companyName,
          tenancyId: this.tenancyId,
        },
      })
    },
    goEdit () {
      if (this.isTenant || this.isMarketing) {
        this.isTenantOrMarketingEditing = true
      } else {
        this.goToEditPage()
      }
    },
    goToEditPage () {
      const tabs = lodash.cloneDeep(tenancyTabsViewToEdit)
      if (this.enableTenantProjectsFeature) {
        tabs.splice(2, 0, tenancyEditTabs.tenancyInformation)
      }
      if (this.enableGreenOperationFeature) {
        const startIndex = this.enableTenantProjectsFeature ? 3 : 2
        tabs.splice(startIndex, 0, tenancyEditTabs.tenancyInformation)
      }
      if (this.enableWorkPermitsFeature) {
        tabs.splice(tabs.length - 1, 0, tenancyEditTabs.tenancyInformation)
      }
      const tabIndex = tabs[this.currentTabIndex]
      const route = {
        name: 'ccdTenancyEdit',
        params: { id: this.tenancyId },
        query: { companyId: this.companyId, tabIndex },
      }
      if (this.tenancyBackPath) {
        this.$router.replace(route)
        return
      }
      this.$router.push(route)
    },
    getCurrentTab (index) {
      this.currentTabIndex = parseInt(index, 10)
    },
    onActiveTab (index) {
      const tabCName = lodash.get(this.tabTemplates, [index, 'tabCName'])
      if (tabCName === ENGINEER_TENANCY_TABS.allProjects) {
        this.$nextTick(() => {
          EventBus.emit('tenancy.allProjectsTab.query')
        })
      }
    },
    cancelEdit () {
      formUtils.resetDirty()
      this.setCurrentTenancy({ tenancy: this.keep })
      this.isTenantOrMarketingEditing = false
    },
    async save () {
      const validResult = this.validateContactData()
      if (!validResult) return false
      this.$refs.personalInfoProtectModal.open(this.saveContacts)
    },
    validateContactData () {
      const componentRefName = this.canTenantEditContact ? 'tenancyAllContactsEdit' : 'tenancyAllContacts'
      const tenancyAllContacts = this.$refs.dynamicTabsContainer.$refs[componentRefName][0]
      let eCircularInvalid = false
      let emergencyInvalid = false
      let fireMarshalInvalid = false
      if (this.canTenantEditContact) {
        eCircularInvalid = tenancyAllContacts.$refs.eCircularContact.isInvalidFloorEmails()
        emergencyInvalid = tenancyAllContacts.$refs.emergencyContact.isInvalid()
        fireMarshalInvalid = !tenancyAllContacts.$refs.fireMarshalContact.validate()
      }
      let marketingInvalid = false
      if (this.isRetail) {
        marketingInvalid = tenancyAllContacts.$refs.marketingContact.isInvalidEmailsTypes()
      }
      const allContactsTabInvalid = eCircularInvalid || emergencyInvalid || fireMarshalInvalid || marketingInvalid
      if (allContactsTabInvalid) {
        return false
      }
      return true
    },
    async saveContacts () {
      const updateContactRequest =
          buildContactRequest(this.currentTenancy.tenancyContacts, this.keep.tenancyContacts, this.keep.companyId)
      if (updateContactRequest.length) {
        await this.updateTenancyContacts({
          tenancyId: this.currentTenancy.id,
          request: updateContactRequest,
        })
        await this.loadTenancy(this.tenancyId)
        EventBus.emit('update_histories_table')
      }

      this.keep = lodash.cloneDeep(this.currentTenancy)

      this.isTenantOrMarketingEditing = false
      return true
    },
    async goToLinkContact (params) {
      let valid = true
      if (this.isPageDirty) {
        const valid = this.validateContactData()
        if (!valid) return false
      }
      if (valid) {
        await this.saveContacts()
        formUtils.resetDirty()
        navigation.toPage(this.isTenant ? 'tenantContacts' : 'contacts', {}, {
          tenancyId: this.currentTenancy.id,
          companyId: this.companyId,
          ...params,
        })
      }
    },
    getSlotName (tabName) {
      const index = this.tenantViewTabNames.findIndex((tenantTabName) => tenantTabName === tabName)
      return `tab-${index}`
    },
    async addTenancyContacts (routeParams, enableContactEditInQuery) {
      if (this.canTenantEditContact || this.isMarketing) {
        this.keep = lodash.cloneDeep(this.currentTenancy)
        await this.addTenancyContactsToStore(routeParams)
      }
      this.isTenantOrMarketingEditing = !!(enableContactEditInQuery || routeParams.enableContactEdit)
      if (this.isTenantOrMarketingEditing) {
        this.currentTabIndex = 1
      }
    },
    syncListNavigationSequenceWithRoute () {
      const cur = String(this.$route.params.id)
      const seq = this.listNavigationSequence || []
      if (!seq.length) return
      // Only sync/clear against tenancy IDs when sequence is from All Tenancy Records (TENANCY_RECORD rows).
      // TPM/HOTB list sequences store project or handover IDs — comparing to `route.params.id` here would
      // clear the store and hide "Back to search results" after View Tenancy Record from handover/detail.
      const allTenancyRecord = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.TENANCY_RECORD)
      if (!allTenancyRecord) return
      if (!seq.some(e => String(e.id) === cur)) {
        this.clearListNavigationSequence()
      }
    },
    showEmptyContactWarn () {
      const hasTenancyRecordPerm = this.accounts.some((account) =>
        account.id === this.currentUser.id &&
        !lodash.isEmpty(account.TenancyAccount.perms.TenancyRecord),
      )
      if (hasTenancyRecordPerm && !(this.visitedTenancyRecord || []).includes(this.currentTenancy.id)) {
        const contactType = TR_EMPTY_CONTACT_WARN_MAP[this.currentTenancy.type] || []
        const showWarn = !contactType.every(t => this.currentTenancy.tenancyContacts.some(c => lodash.get(c, `CompanyContact.${t}`)))
        if (showWarn) {
          this.$refs.emptyContactHint.open()
        }
        this.updateVisitedTR(this.currentTenancy.id)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tabsContainer";
@import "~styles/popup";

.pages-ccd-tenancy-view {
  background: #FBFAFC;

  ::v-deep a.back {
    font-family: Verdana;
    font-size: 14px;
    color: #0c3a58;
  }

  .sticky-header {
    font-size: 0;
    line-height: 0;
    .sticky-btn-height-holder {
      height: 40px;
      width: 1px;
    }
  }

  .sticky-button--group {
    > div {
      margin-right: 1%;
    }
  }

  .tenant-edit {
    margin-right: 10px;
  }

  .tabs-container-wrapper {
    padding: 15px 24px;
  }

  .tabs-container {
    max-width: 1440px;
    margin: 0 auto;
    .hkld-tabs {
      border-bottom: 1px solid #ededed;
      li.tabs-title:first-child {
        width: 250px;
        max-width: 250px;
      }
      li.tabs-title {
        width: 308px;
        max-width: 308px;
        box-shadow: 0 2px 4px 0 rgba(234,234,234,0.50);
        height: 44px;
        line-height: 44px;
      }

      .tabs-title > a {
        background: #fbfafc;
        color: #0C3A58;
        font-size: 16px;
        font-weight: bold;
        letter-spacing: 0;
      }
      .tabs-title.is-active {
        position: relative;
        & > a {
          background-color: #0C3A58;
          color: white;
        }
        &> a:after {
          content: '';
          display: inline-block;
          width: calc(100% - 20px);
          height: 5px;
          background: #0C3A58;
          position: absolute;
          left: 10px;
          top: calc(100% - 5px);
        }
      }
    }
    #sticky-tabs {
      width: 100%;
      .is-active-tab {
        position: relative;
        &:after {
          content: '';
          display: inline-block;
          width: calc(100% - 20px);
          height: 5px;
          background: #0C3A58;
          position: absolute;
          left: 10px;
          top: calc(100% - 5px);
        }
      }
    }
    #common-tabs {
      background-color: #FBFAFC;
    }
    .common-tabs, .tabs-panel {
      padding: 15px 0px;
    }
  }
  .sticky-btn-height-holder {
    .edit {
      margin-right: 15px;
    }
  }
}
</style>

<style lang="scss">
@import '~styles/multi-value';
#create-tenantproject-without-premisespair-hint {
  .customized-modal {
    width: 600px;
  }
}
</style>
